'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { 
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  LineChart, Line
} from 'recharts';
import { 
  Truck, TrendingUp, TrendingDown, DollarSign, Package, 
  Loader2, AlertTriangle, Percent
} from 'lucide-react';

import { DataTable } from '@/components/dashboard/DataTable';
import { 
  fetchCarrierPerformance,
  fetchCarrierTrends,
  formatCurrency, 
  formatPercent
} from '@/lib/api-client';
import type { TableColumn, CarrierPerformance } from '@/types/dashboard';

const COLORS = ['#3B82F6', '#22C55E', '#8B5CF6', '#F59E0B', '#EF4444', '#EC4899', '#14B8A6', '#F97316'];

// Card Component
const Card = ({ children, className = "", isDark = true }: { children: React.ReactNode; className?: string; isDark?: boolean }) => (
  <div className={`backdrop-blur-md border rounded-xl p-5 transition-colors duration-300 ${
    isDark 
      ? 'bg-neutral-900/50 border-neutral-800' 
      : 'bg-white border-gray-200 shadow-sm'
  } ${className}`}>
    {children}
  </div>
);

// Metric Card Component
const MetricCard = ({ title, value, subtitle, icon: Icon, trend, isDark = true }: any) => (
  <Card isDark={isDark}>
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <p className={`text-sm font-medium ${isDark ? 'text-neutral-400' : 'text-gray-600'}`}>
          {title}
        </p>
        <h3 className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {value}
        </h3>
        {subtitle && (
          <p className={`text-xs mt-1 ${isDark ? 'text-neutral-500' : 'text-gray-500'}`}>
            {subtitle}
          </p>
        )}
      </div>
      {Icon && (
        <div className={`p-2 rounded-lg ${isDark ? 'bg-blue-500/10' : 'bg-blue-100'}`}>
          <Icon size={20} className="text-blue-500" />
        </div>
      )}
    </div>
    {trend !== undefined && (
      <div className={`mt-3 flex items-center gap-1 text-sm ${
        trend >= 0 ? 'text-emerald-400' : 'text-rose-400'
      }`}>
        {trend >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
        <span>{Math.abs(trend)}% vs last period</span>
      </div>
    )}
  </Card>
);

export default function FreightPage() {
  const [carriers, setCarriers] = useState<CarrierPerformance[]>([]);
  const [trends, setTrends] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDarkMode] = useState(true);

  // Load carrier data
  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        setError(null);
        
        const [carrierData, trendData] = await Promise.all([
          fetchCarrierPerformance(),
          fetchCarrierTrends(12)
        ]);
        
        setCarriers(carrierData);
        setTrends(trendData);
        console.log('✅ Loaded carriers:', carrierData.length);
      } catch (err) {
        console.error('❌ Failed to load freight data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // Calculate summary metrics
  const summaryMetrics = useMemo(() => {
    if (carriers.length === 0) return null;

    const totalRevenue = carriers.reduce((sum, c) => sum + c.total_revenue, 0);
    const totalCost = carriers.reduce((sum, c) => sum + c.carrier_cost, 0);
    const totalMargin = carriers.reduce((sum, c) => sum + c.carrier_margin, 0);
    const totalShipments = carriers.reduce((sum, c) => sum + c.shipment_count, 0);
    const avgMarginPct = (totalMargin / totalRevenue) * 100;
    
    // Best margin carrier
    const bestMargin = [...carriers].sort((a, b) => b.margin_pct - a.margin_pct)[0];
    
    return {
      totalCarriers: carriers.length,
      totalRevenue,
      totalCost,
      totalMargin,
      avgMarginPct,
      totalShipments,
      avgRevenuePerShipment: totalRevenue / totalShipments,
      bestMarginCarrier: bestMargin
    };
  }, [carriers]);

  // Prepare data for carrier market share pie chart (top 10)
  const pieChartData = useMemo(() => {
    const sortedByRevenue = [...carriers].sort((a, b) => b.total_revenue - a.total_revenue);
    return sortedByRevenue.slice(0, 10).map((carrier, index) => ({
      name: carrier.carrier,
      value: carrier.total_revenue,
      shipments: carrier.shipment_count,
      color: COLORS[index % COLORS.length]
    }));
  }, [carriers]);

  // Prepare data for carrier margin comparison bar chart
  const marginBarData = useMemo(() => {
    return [...carriers]
      .sort((a, b) => b.total_revenue - a.total_revenue)
      .slice(0, 12)
      .map(carrier => ({
        name: (carrier.carrier || 'Unknown').slice(0, 15),
        marginPct: carrier.margin_pct || 0,
        markupPct: carrier.markup_pct || 0,
        revenue: carrier.total_revenue || 0
      }));
  }, [carriers]);

  // Table columns configuration
  const tableColumns: TableColumn[] = [
    {
      key: 'carrier',
      label: 'Carrier',
      sortable: true
    },
    {
      key: 'shipment_count',
      label: 'Shipments',
      sortable: true,
      align: 'right',
      formatter: (val) => val?.toLocaleString()
    },
    {
      key: 'total_revenue',
      label: 'Revenue',
      sortable: true,
      formatter: formatCurrency,
      align: 'right'
    },
    {
      key: 'carrier_cost',
      label: 'Carrier Cost',
      sortable: true,
      formatter: formatCurrency,
      align: 'right'
    },
    {
      key: 'carrier_margin',
      label: 'Margin $',
      sortable: true,
      formatter: formatCurrency,
      align: 'right'
    },
    {
      key: 'margin_pct',
      label: 'Margin %',
      sortable: true,
      formatter: (val) => formatPercent(val, 1),
      align: 'right'
    },
    {
      key: 'markup_pct',
      label: 'Markup %',
      sortable: true,
      formatter: (val) => formatPercent(val, 1),
      align: 'right'
    },
    {
      key: 'avg_revenue_per_shipment',
      label: 'Avg/Shipment',
      sortable: true,
      formatter: formatCurrency,
      align: 'right'
    },
    {
      key: 'client_count',
      label: 'Clients',
      sortable: true,
      align: 'center'
    }
  ];

  return (
    <div className="p-6 space-y-6">
      
      {/* Page Header */}
      <div>
        <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Freight & Carrier Analysis
        </h1>
        <p className={`text-sm ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
          Carrier performance, margins, and shipping volume analysis
        </p>
      </div>

      {/* Loading State */}
      {loading && (
        <Card isDark={isDarkMode} className="flex items-center justify-center py-12">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-blue-500" />
            <p className={`text-lg font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Loading Freight Data...
            </p>
          </div>
        </Card>
      )}

      {/* Error State */}
      {error && !loading && (
        <Card isDark={isDarkMode} className="border-rose-500/50 bg-rose-500/10">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-rose-500 mt-0.5 shrink-0" size={20} />
            <div>
              <h3 className="font-semibold text-rose-400 mb-1">Failed to Load Data</h3>
              <p className={`text-sm ${isDarkMode ? 'text-neutral-400' : 'text-gray-600'}`}>
                {error}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Content - Only show when data is loaded */}
      {!loading && !error && summaryMetrics && (
        <>
          {/* Summary Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              title="Total Freight Revenue"
              value={formatCurrency(summaryMetrics.totalRevenue)}
              subtitle="All carriers combined"
              icon={DollarSign}
              isDark={isDarkMode}
            />
            <MetricCard
              title="Total Shipments"
              value={summaryMetrics.totalShipments.toLocaleString()}
              subtitle={`${summaryMetrics.totalCarriers} active carriers`}
              icon={Package}
              isDark={isDarkMode}
            />
            <MetricCard
              title="Avg Margin"
              value={formatPercent(summaryMetrics.avgMarginPct, 1)}
              subtitle={`Margin: ${formatCurrency(summaryMetrics.totalMargin)}`}
              icon={Percent}
              isDark={isDarkMode}
            />
            <MetricCard
              title="Avg per Shipment"
              value={formatCurrency(summaryMetrics.avgRevenuePerShipment)}
              subtitle="Revenue per shipment"
              icon={Truck}
              isDark={isDarkMode}
            />
          </div>

          {/* Charts Row 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Carrier Revenue Share Pie Chart */}
            <Card isDark={isDarkMode}>
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Top 10 Carriers by Revenue
              </h3>
              <div className="h-80 relative">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {pieChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: isDarkMode ? '#171717' : '#FFFFFF',
                        borderColor: isDarkMode ? '#262626' : '#E5E7EB',
                        borderRadius: '8px'
                      }}
                      formatter={(value: any, name: any, props: any) => [
                        `${formatCurrency(value)} (${props.payload.shipments.toLocaleString()} shipments)`,
                        props.payload.name
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2">
                {pieChartData.slice(0, 10).map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-xs">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                    <span className={`truncate ${isDarkMode ? 'text-neutral-400' : 'text-gray-600'}`}>
                      {item.name}
                    </span>
                    <span className={`ml-auto font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {formatCurrency(item.value)}
                    </span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Carrier Margin Comparison Bar Chart */}
            <Card isDark={isDarkMode}>
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Margin % by Carrier
              </h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={marginBarData} layout="horizontal">
                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#262626' : '#E5E7EB'} />
                    <XAxis 
                      type="number" 
                      stroke={isDarkMode ? '#525252' : '#9CA3AF'}
                      tick={{fontSize: 11}}
                      tickFormatter={(val) => `${val}%`}
                    />
                    <YAxis 
                      type="category" 
                      dataKey="name" 
                      stroke={isDarkMode ? '#525252' : '#9CA3AF'}
                      tick={{fontSize: 10}}
                      width={80}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: isDarkMode ? '#171717' : '#FFFFFF',
                        borderColor: isDarkMode ? '#262626' : '#E5E7EB',
                        color: isDarkMode ? '#fff' : '#000'
                      }}
                      formatter={(value: any, name: string) => {
                        if (name === 'marginPct' || name === 'markupPct') return `${value.toFixed(1)}%`;
                        return formatCurrency(value);
                      }}
                    />
                    <Legend />
                    <Bar dataKey="marginPct" fill="#22C55E" name="Margin %" />
                    <Bar dataKey="markupPct" fill="#3B82F6" name="Markup %" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          {/* Best Performing Carrier Highlight */}
          {summaryMetrics.bestMarginCarrier && (
            <Card isDark={isDarkMode} className="border-emerald-500/30 bg-emerald-500/5">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${isDarkMode ? 'text-neutral-400' : 'text-gray-600'}`}>
                    🏆 Best Margin Carrier
                  </p>
                  <h3 className={`text-xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {summaryMetrics.bestMarginCarrier.carrier}
                  </h3>
                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
                    {summaryMetrics.bestMarginCarrier.shipment_count.toLocaleString()} shipments • {formatCurrency(summaryMetrics.bestMarginCarrier.total_revenue)} revenue
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-emerald-400">
                    {formatPercent(summaryMetrics.bestMarginCarrier.margin_pct, 1)}
                  </div>
                  <p className={`text-xs ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
                    Margin Percentage
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* Carrier Performance Table */}
          <Card isDark={isDarkMode}>
            <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              All Carriers Performance
            </h3>
            <DataTable
              columns={tableColumns}
              data={carriers}
              pageSize={15}
              isDark={isDarkMode}
              searchable={true}
              searchPlaceholder="Search carriers..."
              emptyMessage="No carriers found"
            />
          </Card>
        </>
      )}
    </div>
  );
}
